import React, { useEffect, useState } from "react";
import LoadingAM from "../assets/rigve_loader.gif";
import { Phone, Mail, MapPin, ArrowRight, Home } from "lucide-react";
import { main } from "framer-motion/client";

export const ContactPage = () => {
  const [ContactPageData, setContactPageData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
    consent: false,
  });

  const [errors, setErrors] = useState({}); // ✅ added

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          "https://webadmin.rigve.com/api/get_extra_details",
        );
        const result = await response.json();

        if (result.status === 1) {
          setContactPageData(result.data);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();

    setTimeout(() => {
      setIsLoading(false);
    }, 1500);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "phone" && value.length > 10) return;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // ✅ clear error while typing
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Enter valid 10 digit phone number";
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Enter valid email address";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    if (!formData.consent) {
      newErrors.consent = "Please accept this field";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const response = await fetch(
        `https://webadmin.rigve.com/api/save_contact?name=${encodeURIComponent(
          formData.name,
        )}&phone=${encodeURIComponent(
          formData.phone,
        )}&email=${encodeURIComponent(
          formData.email,
        )}&msg=${encodeURIComponent(formData.message)}`,
      );

      const result = await response.json();

      if (result.status === 1) {
        alert("Submitted Successfully");
        setFormData({
          name: "",
          phone: "",
          email: "",
          message: "",
          consent: false,
        });
        setErrors({});
      }
    } catch (error) {
      console.error(error);
    }
  };
  if (isLoading) {
    return (
      <div className="w-full min-h-screen flex justify-center items-center bg-gradient-to-r from-yellow-50 to-orange-50">
        <img src={LoadingAM} className="w-20 h-20" alt="loading" />
      </div>
    );
  }

  return (
    <main>
      <section className="bg-gradient-to-b from-white via-yellow-50 to-orange-50 text-gray-900 overflow-hidden">
        {/* CONTACT SECTION */}
        <div className="px-4 mx-auto max-w-7xl py-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* LEFT INFO */}
            <div>
              <h2 className="text-2xl font-bold">
                Let’s Build Something Beautiful
              </h2>

              <p className="mt-4 text-gray-600">
                Contact our team for custom wall texture solutions, premium
                finishes and elegant interior transformations.
              </p>

              <div className="space-y-5 mt-10">
                <div className="bg-white rounded-2xl p-6 shadow-md flex flex-row items-start text-left gap-4">
                  <Mail className="text-orange-500 w-6 h-6 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-500">Email Address</p>
                    <a
                      href={`mailto:${ContactPageData?.email}`}
                      className="font-semibold hover:text-orange-500"
                    >
                      {ContactPageData?.email}
                    </a>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-md flex flex-row items-start text-left gap-4">
                  <Phone className="text-orange-500 w-6 h-6 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-500">Phone Number</p>
                    <a
                      href={`tel:${ContactPageData?.phone}`}
                      className="font-semibold hover:text-orange-500"
                    >
                      {ContactPageData?.phone}
                    </a>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-md flex flex-row items-start text-left gap-4">
                  <Home className="text-orange-500 w-6 h-6 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-500">Our Address</p>
                    <a
                      href={ContactPageData?.address_1_link}
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold hover:text-orange-500"
                    >
                      {ContactPageData?.address1}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT FORM */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="fs-21 font-bold">Send Message</h3>
              <p className="text-gray-500 mt-2">
                Fill your details and we’ll contact you soon.
              </p>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full p-4 text-sm rounded-2xl border border-gray-200"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full p-4 text-sm rounded-2xl border border-gray-200"
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm">{errors.phone}</p>
                    )}
                  </div>
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full p-4 text-sm rounded-2xl border border-gray-200"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm">{errors.email}</p>
                  )}
                </div>

                <div>
                  <textarea
                    name="message"
                    rows="5"
                    placeholder="Your Message"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full p-4 text-sm rounded-2xl border border-gray-200"
                  ></textarea>
                  {errors.message && (
                    <p className="text-red-500 text-sm">{errors.message}</p>
                  )}
                </div>

                <div>
                  <label className="flex gap-3 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      name="consent"
                      checked={formData.consent}
                      onChange={handleChange}
                    />
                    I agree to receive updates & offers.
                  </label>
                  {errors.consent && (
                    <p className="text-red-500 text-sm">{errors.consent}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full text-md py-4 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-2xl font-semibold flex items-center justify-center gap-2"
                >
                  Get In Touch <ArrowRight size={18} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
